import { useState, useEffect, useRef, useCallback } from 'react'
import { ZoomIn, ZoomOut, RotateCcw, Play, Pause } from 'lucide-react'

/**
 * AudioWaveform - Clean waveform visualization matching dashboard design
 * Fits container at 1x zoom, scrollable when zoomed in
 */
const AudioWaveform = ({
  audioUrl,
  isPlaying = false,
  currentTime = 0,
  duration = 0,
  onSeek = null,
  onPlay = null,
  height = 64,
  className = ''
}) => {
  const [waveformData, setWaveformData] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [zoom, setZoom] = useState(1)
  const [audioDuration, setAudioDuration] = useState(duration)
  const containerRef = useRef(null)
  const waveformRef = useRef(null)

  // Fixed number of bars that fit nicely in the container
  const BASE_BARS = 100

  // Analyze audio file
  const analyzeAudio = useCallback(async (url) => {
    if (!url) return

    setIsLoading(true)
    setError(null)

    try {
      const response = await fetch(url)
      if (!response.ok) throw new Error('Failed to fetch audio')

      const arrayBuffer = await response.arrayBuffer()
      const audioContext = new (window.AudioContext || window.webkitAudioContext)()
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer)

      const rawData = audioBuffer.getChannelData(0)
      const totalDuration = audioBuffer.duration
      setAudioDuration(totalDuration)

      // Always analyze at high resolution
      const analysisResolution = 500
      const samplesPerBar = Math.floor(rawData.length / analysisResolution)

      const peaks = []
      for (let i = 0; i < analysisResolution; i++) {
        const start = i * samplesPerBar
        const end = Math.min(start + samplesPerBar, rawData.length)

        let max = 0
        for (let j = start; j < end; j++) {
          const abs = Math.abs(rawData[j])
          if (abs > max) max = abs
        }
        peaks.push(max)
      }

      // Normalize
      const maxPeak = Math.max(...peaks, 0.01)
      const normalizedPeaks = peaks.map(p => p / maxPeak)

      setWaveformData(normalizedPeaks)
      audioContext.close()
    } catch (err) {
      console.error('Waveform analysis failed:', err)
      setError(err.message)
      setWaveformData([])
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    if (audioUrl) {
      analyzeAudio(audioUrl)
    }
  }, [audioUrl, analyzeAudio])

  // Resample data to fit display
  const getDisplayData = useCallback(() => {
    if (waveformData.length === 0) return []

    const targetBars = Math.floor(BASE_BARS * zoom)
    const result = []

    for (let i = 0; i < targetBars; i++) {
      const startIdx = Math.floor((i / targetBars) * waveformData.length)
      const endIdx = Math.floor(((i + 1) / targetBars) * waveformData.length)

      let max = 0
      for (let j = startIdx; j < endIdx && j < waveformData.length; j++) {
        if (waveformData[j] > max) max = waveformData[j]
      }
      result.push(max)
    }

    return result
  }, [waveformData, zoom])

  const displayData = getDisplayData()
  const effectiveDuration = audioDuration || duration
  const progress = effectiveDuration > 0 ? (currentTime / effectiveDuration) : 0

  // Calculate silence metrics
  const getSilenceInfo = useCallback(() => {
    if (waveformData.length === 0 || !effectiveDuration) return null

    const threshold = 0.05
    const msPerSample = (effectiveDuration * 1000) / waveformData.length

    let leadMs = 0
    for (let i = 0; i < waveformData.length; i++) {
      if (waveformData[i] > threshold) break
      leadMs = (i + 1) * msPerSample
    }

    let trailMs = 0
    for (let i = waveformData.length - 1; i >= 0; i--) {
      if (waveformData[i] > threshold) break
      trailMs = (waveformData.length - i) * msPerSample
    }

    return {
      lead: Math.round(leadMs),
      trail: Math.round(trailMs),
      hasLead: leadMs > 50,
      hasTrail: trailMs > 50
    }
  }, [waveformData, effectiveDuration])

  const silenceInfo = getSilenceInfo()

  // Handle click to seek
  const handleSeek = (e) => {
    if (!onSeek || !effectiveDuration) return

    const rect = waveformRef.current?.getBoundingClientRect()
    if (!rect) return

    const scrollLeft = waveformRef.current?.parentElement?.scrollLeft || 0
    const x = e.clientX - rect.left + scrollLeft
    const width = rect.width
    const pct = Math.min(1, Math.max(0, x / width))
    onSeek(pct * effectiveDuration)
  }

  // Format time
  const formatTime = (s) => {
    if (!s || isNaN(s)) return '0:00'
    const m = Math.floor(s / 60)
    const sec = Math.floor(s % 60)
    return `${m}:${sec.toString().padStart(2, '0')}`
  }

  const formatMs = (ms) => ms < 1000 ? `${ms}ms` : `${(ms/1000).toFixed(1)}s`

  return (
    <div
      ref={containerRef}
      className={`audio-waveform-container ${className}`}
      style={{
        background: 'var(--bg-card)',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--border-color)',
        overflow: 'hidden'
      }}
    >
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '10px 14px',
        borderBottom: '1px solid var(--border-color)',
        gap: '12px',
        flexWrap: 'wrap'
      }}>
        {/* Left side - Play & Time */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {onPlay && (
            <button
              onClick={onPlay}
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                border: 'none',
                background: isPlaying
                  ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)'
                  : 'linear-gradient(135deg, var(--primary) 0%, #b91c1c 100%)',
                color: 'white',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
              }}
            >
              {isPlaying ? <Pause size={16} /> : <Play size={16} style={{ marginLeft: '2px' }} />}
            </button>
          )}

          <div>
            <div style={{
              fontSize: '0.9rem',
              fontWeight: '600',
              color: 'var(--text-primary)',
              fontFamily: 'monospace'
            }}>
              {formatTime(currentTime)} <span style={{ color: 'var(--text-muted)' }}>/</span> {formatTime(effectiveDuration)}
            </div>
          </div>
        </div>

        {/* Right side - Status & Zoom */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* Silence indicators */}
          {silenceInfo?.hasLead && (
            <span style={{
              fontSize: '0.7rem',
              padding: '3px 8px',
              borderRadius: '12px',
              background: silenceInfo.lead > 200 ? 'rgba(239, 68, 68, 0.15)' : 'rgba(245, 158, 11, 0.15)',
              color: silenceInfo.lead > 200 ? '#ef4444' : '#f59e0b',
              fontWeight: '500'
            }}>
              Lead: {formatMs(silenceInfo.lead)}
            </span>
          )}
          {silenceInfo?.hasTrail && (
            <span style={{
              fontSize: '0.7rem',
              padding: '3px 8px',
              borderRadius: '12px',
              background: silenceInfo.trail > 200 ? 'rgba(239, 68, 68, 0.15)' : 'rgba(245, 158, 11, 0.15)',
              color: silenceInfo.trail > 200 ? '#ef4444' : '#f59e0b',
              fontWeight: '500'
            }}>
              Trail: {formatMs(silenceInfo.trail)}
            </span>
          )}
          {!silenceInfo?.hasLead && !silenceInfo?.hasTrail && !isLoading && (
            <span style={{
              fontSize: '0.7rem',
              padding: '3px 8px',
              borderRadius: '12px',
              background: 'rgba(34, 197, 94, 0.15)',
              color: '#22c55e',
              fontWeight: '500'
            }}>
              ✓ Clean
            </span>
          )}

          {/* Zoom controls */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '2px',
            background: 'var(--bg-secondary)',
            padding: '4px 6px',
            borderRadius: '8px',
            marginLeft: '4px'
          }}>
            <button
              onClick={() => setZoom(z => Math.max(1, z / 2))}
              disabled={zoom <= 1}
              style={{
                background: 'none',
                border: 'none',
                padding: '4px',
                cursor: zoom <= 1 ? 'not-allowed' : 'pointer',
                opacity: zoom <= 1 ? 0.3 : 0.7,
                color: 'var(--text-primary)',
                display: 'flex',
                borderRadius: '4px'
              }}
            >
              <ZoomOut size={14} />
            </button>
            <span style={{
              fontSize: '0.7rem',
              color: 'var(--text-muted)',
              minWidth: '28px',
              textAlign: 'center'
            }}>
              {zoom}x
            </span>
            <button
              onClick={() => setZoom(z => Math.min(8, z * 2))}
              disabled={zoom >= 8}
              style={{
                background: 'none',
                border: 'none',
                padding: '4px',
                cursor: zoom >= 8 ? 'not-allowed' : 'pointer',
                opacity: zoom >= 8 ? 0.3 : 0.7,
                color: 'var(--text-primary)',
                display: 'flex',
                borderRadius: '4px'
              }}
            >
              <ZoomIn size={14} />
            </button>
            {zoom > 1 && (
              <button
                onClick={() => setZoom(1)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '4px',
                  cursor: 'pointer',
                  opacity: 0.5,
                  color: 'var(--text-muted)',
                  display: 'flex',
                  borderRadius: '4px'
                }}
              >
                <RotateCcw size={12} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Waveform Area */}
      <div
        style={{
          overflowX: zoom > 1 ? 'auto' : 'hidden',
          overflowY: 'hidden',
          padding: '16px',
          background: 'var(--bg-secondary)'
        }}
      >
        {isLoading ? (
          <div style={{
            height: `${height}px`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--text-muted)',
            fontSize: '0.85rem'
          }}>
            Analyzing...
          </div>
        ) : error ? (
          <div style={{
            height: `${height}px`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--danger)',
            fontSize: '0.85rem'
          }}>
            Failed to load
          </div>
        ) : (
          <div
            ref={waveformRef}
            onClick={handleSeek}
            style={{
              height: `${height}px`,
              width: zoom > 1 ? `${zoom * 100}%` : '100%',
              position: 'relative',
              cursor: onSeek ? 'pointer' : 'default',
              display: 'flex',
              alignItems: 'center',
              gap: '1px'
            }}
          >
            {/* Waveform bars */}
            {displayData.map((peak, i) => {
              const barHeight = Math.max(2, peak * height * 0.85)
              const pct = i / displayData.length
              const isPlayed = pct < progress
              const isSilent = peak < 0.05

              return (
                <div
                  key={i}
                  style={{
                    flex: '1',
                    height: `${barHeight}px`,
                    background: isPlayed
                      ? 'linear-gradient(180deg, #22c55e 0%, #16a34a 100%)'
                      : isSilent
                        ? 'rgba(150, 150, 150, 0.2)'
                        : 'linear-gradient(180deg, rgba(99, 102, 241, 0.6) 0%, rgba(79, 70, 229, 0.4) 100%)',
                    borderRadius: '2px',
                    transition: isPlaying ? 'none' : 'background 0.15s ease'
                  }}
                />
              )
            })}

            {/* Progress line */}
            <div
              style={{
                position: 'absolute',
                left: `${progress * 100}%`,
                top: '-4px',
                bottom: '-4px',
                width: '2px',
                background: '#22c55e',
                borderRadius: '1px',
                boxShadow: '0 0 8px rgba(34, 197, 94, 0.6)',
                pointerEvents: 'none',
                transition: isPlaying ? 'left 0.1s linear' : 'none'
              }}
            />
          </div>
        )}
      </div>

      {/* Footer - Time markers */}
      {effectiveDuration > 0 && !isLoading && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: '6px 16px 10px',
          fontSize: '0.65rem',
          color: 'var(--text-muted)',
          borderTop: '1px solid var(--border-color)'
        }}>
          <span>0:00</span>
          <span>{formatTime(effectiveDuration * 0.25)}</span>
          <span>{formatTime(effectiveDuration * 0.5)}</span>
          <span>{formatTime(effectiveDuration * 0.75)}</span>
          <span>{formatTime(effectiveDuration)}</span>
        </div>
      )}
    </div>
  )
}

export default AudioWaveform
